import React, { useState, useEffect, useCallback } from 'react'
import dayjs from 'dayjs'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'

function buildCalendarDays(viewDate, startDate, endDate, anchor) {
    const startOfMonth = viewDate.startOf('month')
    const endOfMonth = viewDate.endOf('month')
    const startOfCalendar = startOfMonth.startOf('week')
    const endOfCalendar = endOfMonth.endOf('week')

    const lo = startDate.startOf('day')
    const hi = endDate.startOf('day')
    const rangeLow = lo.isBefore(hi) ? lo : hi
    const rangeHigh = lo.isBefore(hi) ? hi : lo
    const hasPending = anchor !== null

    const days = []
    let current = startOfCalendar

    while (current.isBefore(endOfCalendar, 'day') || current.isSame(endOfCalendar, 'day')) {
        const d = current.startOf('day')
        const isPendingAnchor = hasPending && d.isSame(anchor, 'day')
        const inCommittedRange = !hasPending && !d.isBefore(rangeLow) && !d.isAfter(rangeHigh)
        const isRangeStart = !hasPending && d.isSame(rangeLow, 'day')
        const isRangeEnd = !hasPending && d.isSame(rangeHigh, 'day')
        const isInRangeMiddle = inCommittedRange && !isRangeStart && !isRangeEnd

        days.push({
            date: current,
            isCurrentMonth: current.isSame(viewDate, 'month'),
            isToday: current.isSame(dayjs(), 'day'),
            isPendingAnchor,
            isRangeEdge: isRangeStart || isRangeEnd,
            isInRangeMiddle
        })
        current = current.add(1, 'day')
    }

    return days
}

function getDayCellClass(day, mobile = false) {
    const { isPendingAnchor, isRangeEdge, isInRangeMiddle, isToday, isCurrentMonth } = day
    const classes = ['shop-date-range__day']
    if (mobile) classes.push('shop-date-range__day--mobile')
    if (isPendingAnchor || isRangeEdge) classes.push('shop-date-range__day--edge')
    else if (isInRangeMiddle) classes.push('shop-date-range__day--in-range')
    else if (isToday) classes.push('shop-date-range__day--today')
    if (!isCurrentMonth && !isPendingAnchor && !isRangeEdge && !isInRangeMiddle) {
        classes.push('shop-date-range__day--outside')
    }
    return classes.join(' ')
}

function DateRangeCalendarBody({
    viewDate,
    startDate,
    endDate,
    anchor,
    mobile,
    onDayClick,
    onNavigateMonth,
    onNavigateYear,
    onClear,
    onToday
}) {
    return (
        <>
            <div className={`shop-date-range__hint${anchor ? ' shop-date-range__hint--active' : ''}`}>
                {anchor ? 'Second click: end date' : 'First click: start date'}
            </div>

            <div className="shop-date-range__nav">
                <button type="button" className="shop-date-range__nav-btn" onClick={() => onNavigateYear('prev')}>⟪</button>
                <button type="button" className="shop-date-range__nav-btn" onClick={() => onNavigateMonth('prev')}>‹</button>
                <span className="shop-date-range__month-title">{viewDate.format('MMMM YYYY')}</span>
                <button type="button" className="shop-date-range__nav-btn" onClick={() => onNavigateMonth('next')}>›</button>
                <button type="button" className="shop-date-range__nav-btn" onClick={() => onNavigateYear('next')}>⟫</button>
            </div>

            <div className="shop-date-range__weekdays">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((dow) => (
                    <div key={dow} className="shop-date-range__weekday">{dow}</div>
                ))}
            </div>

            <div className="shop-date-range__days">
                {buildCalendarDays(viewDate, startDate, endDate, anchor).map((day, index) => (
                    <button
                        type="button"
                        key={index}
                        className={getDayCellClass(day, mobile)}
                        onClick={() => onDayClick(day.date)}
                    >
                        {day.date.format('D')}
                    </button>
                ))}
            </div>

            <div className="shop-date-range__footer">
                <button type="button" className="shop-date-range__btn shop-date-range__btn--outline" onClick={onClear}>
                    Clear
                </button>
                <button type="button" className="shop-date-range__btn shop-date-range__btn--primary" onClick={onToday}>
                    Today
                </button>
            </div>
        </>
    )
}

function useDateRangePickerState(startDate, onRangeChange, getResetRange) {
    const [isOpen, setIsOpen] = useState(false)
    const [viewDate, setViewDate] = useState(startDate)
    const [anchor, setAnchor] = useState(null)

    const close = useCallback(() => setIsOpen(false), [])

    const toggleOpen = useCallback(() => {
        setIsOpen((prev) => {
            const next = !prev
            if (next) {
                setViewDate(startDate)
                setAnchor(null)
            }
            return next
        })
    }, [startDate])

    const handleDayClick = useCallback((d) => {
        const picked = d.startOf('day')
        if (anchor === null) {
            setAnchor(picked)
            return
        }
        const lo = anchor.isBefore(picked) ? anchor : picked
        const hi = anchor.isBefore(picked) ? picked : anchor
        onRangeChange(lo, hi)
        setAnchor(null)
        setIsOpen(false)
    }, [anchor, onRangeChange])

    const handleClear = useCallback(() => {
        if (typeof getResetRange === 'function') {
            const { start, end } = getResetRange()
            onRangeChange(start, end)
        } else {
            onRangeChange(dayjs().subtract(7, 'day'), dayjs())
        }
        setAnchor(null)
        setIsOpen(false)
    }, [getResetRange, onRangeChange])

    const handleToday = useCallback(() => {
        const t = dayjs().startOf('day')
        onRangeChange(t, t)
        setAnchor(null)
        setIsOpen(false)
    }, [onRangeChange])

    const navigateMonth = useCallback((direction) => {
        setViewDate((v) => (direction === 'prev' ? v.subtract(1, 'month') : v.add(1, 'month')))
    }, [])

    const navigateYear = useCallback((direction) => {
        setViewDate((v) => (direction === 'prev' ? v.subtract(1, 'year') : v.add(1, 'year')))
    }, [])

    return {
        isOpen,
        viewDate,
        anchor,
        toggleOpen,
        close,
        handleDayClick,
        handleClear,
        handleToday,
        navigateMonth,
        navigateYear
    }
}

/** Десктоп: одно поле, выпадающий календарь — первый клик «от», второй «до». */
export function HistoryDesktopDateRangePicker({ label, startDate, endDate, onRangeChange, getResetRange }) {
    const {
        isOpen,
        viewDate,
        anchor,
        toggleOpen,
        close,
        handleDayClick,
        handleClear,
        handleToday,
        navigateMonth,
        navigateYear
    } = useDateRangePickerState(startDate, onRangeChange, getResetRange)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.history-date-range-desktop')) {
                close()
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen, close])

    const formatRange = () => `${startDate.format('MM/DD/YYYY')} — ${endDate.format('MM/DD/YYYY')}`

    return (
        <div
            className="form-group history-date-range-desktop"
            style={{ minWidth: '260px', flex: '0 1 280px', position: 'relative' }}
        >
            <label className="form-label">{label}</label>
            <div
                className="form-input"
                onClick={toggleOpen}
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px'
                }}
            >
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatRange()}
                </span>
                <CalendarMonthIcon className="shop-date-range__trigger-icon" aria-hidden />
            </div>

            {isOpen && (
                <div
                    className="shop-date-range__panel"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        zIndex: 1000,
                        marginTop: '4px',
                        width: '280px',
                        minWidth: '280px'
                    }}
                >
                    <DateRangeCalendarBody
                        viewDate={viewDate}
                        startDate={startDate}
                        endDate={endDate}
                        anchor={anchor}
                        onDayClick={handleDayClick}
                        onNavigateMonth={navigateMonth}
                        onNavigateYear={navigateYear}
                        onClear={handleClear}
                        onToday={handleToday}
                    />
                </div>
            )}
        </div>
    )
}

/** Мобильный: то же поведение, календарь в модальном окне. */
export function HistoryMobileDateRangePicker({ label, startDate, endDate, onRangeChange, getResetRange }) {
    const {
        isOpen,
        viewDate,
        anchor,
        toggleOpen,
        close,
        handleDayClick,
        handleClear,
        handleToday,
        navigateMonth,
        navigateYear
    } = useDateRangePickerState(startDate, onRangeChange, getResetRange)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.history-date-range-modal')) {
                close()
            }
        }
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen, close])

    const formatRange = () => `${startDate.format('MM/DD/YYYY')} — ${endDate.format('MM/DD/YYYY')}`

    return (
        <div className="mobile-filter-group history-date-range-mobile" style={{ position: 'relative' }}>
            <label className="mobile-filter-label">{label}</label>
            <div
                className="mobile-filter-input"
                onClick={toggleOpen}
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '6px'
                }}
            >
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {formatRange()}
                </span>
                <CalendarMonthIcon className="shop-date-range__trigger-icon" aria-hidden />
            </div>

            {isOpen && (
                <>
                    <div className="shop-date-range__overlay" onClick={close} aria-hidden />
                    <div
                        className="history-date-range-modal shop-date-range__panel shop-date-range__panel--modal"
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10000
                        }}
                    >
                        <DateRangeCalendarBody
                            viewDate={viewDate}
                            startDate={startDate}
                            endDate={endDate}
                            anchor={anchor}
                            mobile
                            onDayClick={handleDayClick}
                            onNavigateMonth={navigateMonth}
                            onNavigateYear={navigateYear}
                            onClear={handleClear}
                            onToday={handleToday}
                        />
                    </div>
                </>
            )}
        </div>
    )
}
